/* eslint-disable */
import { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { searchPhotos, getUseAIPhotoFilter } from "../utils/pexels";
import { Icon } from "../utils/icons";
import { COLOR, FONT } from "../utils/theme";
import { authenticatedFetch } from "../utils/apiClient";

const API_URL = process.env.REACT_APP_PROXY_URL || 'http://localhost:3001';

const labelStyle = {
  fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono,
  letterSpacing: "0.1em", textTransform: "uppercase",
  marginBottom: 5, display: "block",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: COLOR.white, border: `1px solid ${COLOR.borderWarm}`,
  borderRadius: 8, padding: "10px 12px",
  color: COLOR.inkDark, fontSize: 14,
  fontFamily: FONT.serif,
  outline: "none", transition: "border-color 0.15s",
};
const focus = e => e.target.style.borderColor = COLOR.mauve;
const blur  = e => e.target.style.borderColor = COLOR.borderWarm;

// Compute crop center as object-position percentages
function cropToObjectPosition(crop) {
  if (!crop || !crop.width) return null;
  const x = crop.unit === "%" ? crop.x + crop.width / 2 : null;
  const y = crop.unit === "%" ? crop.y + crop.height / 2 : null;
  if (x == null) return null;
  return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
}

// Crop an image element to a canvas blob
async function cropToBlob(imgEl, crop) {
  const canvas = document.createElement("canvas");
  const scaleX = imgEl.naturalWidth / imgEl.width;
  const scaleY = imgEl.naturalHeight / imgEl.height;

  let px;
  if (crop.unit === "%") {
    px = {
      x: (crop.x / 100) * imgEl.width,
      y: (crop.y / 100) * imgEl.height,
      w: (crop.width / 100) * imgEl.width,
      h: (crop.height / 100) * imgEl.height,
    };
  } else {
    px = { x: crop.x, y: crop.y, w: crop.width, h: crop.height };
  }

  canvas.width = Math.round(px.w * scaleX);
  canvas.height = Math.round(px.h * scaleY);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    imgEl,
    px.x * scaleX, px.y * scaleY, px.w * scaleX, px.h * scaleY,
    0, 0, canvas.width, canvas.height
  );
  return new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", 0.92));
}

const TABS = [
  { id: "pick",   label: <><Icon name="imagePick" size={14} color={COLOR.inkMid} style={{ marginRight: 3, verticalAlign: "middle" }} /> Pick</> },
  { id: "search", label: <><Icon name="imageSearch" size={14} color={COLOR.inkMid} style={{ marginRight: 3, verticalAlign: "middle" }} /> Manual Search</> },
  { id: "upload", label: <><Icon name="imageUpload" size={14} color={COLOR.inkMid} style={{ marginRight: 3, verticalAlign: "middle" }} /> Upload</> },
  { id: "crop",   label: <><Icon name="imageCrop" size={14} color={COLOR.inkMid} style={{ marginRight: 3, verticalAlign: "middle" }} /> Crop</> },
];

export default function ImageEditor({ currentUrl, aspectRatio = 16 / 9, onSave, onCancel, tripId, activityTitle = "", city = "", activityLocation = "", context = "activity" }) {
  const [activeTab, setActiveTab] = useState("pick");

  // Pick tab
  const [pickResults, setPickResults] = useState([]);
  const [pickLoading, setPickLoading] = useState(false);
  const [pickSelectedIdx, setPickSelectedIdx] = useState(null);
  const pickSearchedRef = useRef(false);

  // Search tab
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  // Selected image — refs guarantee handleSave always reads latest value
  const selectedUrlRef    = useRef(currentUrl || null);
  const selectedSourceRef = useRef(currentUrl ? "search" : null);
  const selectedFileRef   = useRef(null);

  const [selectedUrl,    _setSelectedUrl]    = useState(currentUrl || null);
  const [selectedSource, _setSelectedSource] = useState(currentUrl ? "search" : null);
  const [selectedFile,   _setSelectedFile]   = useState(null);

  const setSelectedUrl    = (url) => { selectedUrlRef.current = url;    _setSelectedUrl(url); };
  const setSelectedSource = (s)   => { selectedSourceRef.current = s;  _setSelectedSource(s); };
  const setSelectedFile   = (f)   => { selectedFileRef.current = f;    _setSelectedFile(f); };

  // Crop tab
  const [crop, setCrop] = useState(null);
  const completedCropRef = useRef(null);
  const [completedCrop, _setCompletedCrop] = useState(null);
  const setCompletedCrop = (c) => { completedCropRef.current = c; _setCompletedCrop(c); };
  const imgRef = useRef(null);

  // Saving / error
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Revoke blob URL on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (selectedUrlRef.current && selectedUrlRef.current.startsWith("blob:")) {
        URL.revokeObjectURL(selectedUrlRef.current);
      }
    };
  }, []);

  // ── Pick tab — auto-search on mount ──────────────────────────────────────────

  useEffect(() => {
    if (pickSearchedRef.current || !activityTitle) return;
    pickSearchedRef.current = true;
    setPickLoading(true);

    async function fetchPick() {
      const locName = activityLocation.replace(/\(.*?\)/g, "").trim();
      const pickQuery = [locName, city].filter(Boolean).join(" ") || activityTitle.trim();
      const results = await searchPhotos(pickQuery, 5);
      if (getUseAIPhotoFilter() && results.length > 0) {
        try {
          const filterRes = await authenticatedFetch(`${API_URL}/api/filter-photos`, {
            method: "POST",
            body: JSON.stringify({
              candidates: results.map(p => ({ url: p.large || p.url, id: p.id })),
              activityTitle: activityTitle.trim(),
              city,
            }),
          });
          if (filterRes.ok) {
            const best = await filterRes.json();
            // Move the AI-picked result to the front
            const reordered = [...results];
            const bestIdx = reordered.findIndex(r => r.id === best.id);
            if (bestIdx > 0) {
              const [picked] = reordered.splice(bestIdx, 1);
              reordered.unshift(picked);
            }
            setPickResults(reordered);
            setPickLoading(false);
            return;
          }
        } catch {
          // fall through to unfiltered results
        }
      }
      setPickResults(results);
      setPickLoading(false);
    }
    fetchPick();
  }, [activityTitle]);

  const handlePickSelect = (photo, idx) => {
    setPickSelectedIdx(idx);
    setSelectedUrl(photo.url);
    setSelectedSource("search");
    setSelectedFile(null);
    setCrop(null);
    setCompletedCrop(null);
  };

  // ── Search ──────────────────────────────────────────────────────────────────

  const handleSearch = async () => {
    if (!searchQuery.trim() || searching) return;
    setSearching(true);
    setSearchError("");
    setSearchResults([]);
    try {
      const results = await searchPhotos(searchQuery.trim(), 6);
      if (results.length === 0) setSearchError("No photos found — try different keywords");
      else setSearchResults(results);
    } catch (e) {
      console.error("Photo search failed:", e);
      setSearchError("Search failed — please try again");
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectSearchPhoto = (photo) => {
    setSelectedUrl(photo.url);
    setSelectedSource("search");
    setSelectedFile(null);
    setCrop(null);
    setCompletedCrop(null);
    setActiveTab("crop");
  };

  // ── Upload ──────────────────────────────────────────────────────────────────

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      setSaveError("Image too large — please use an image under 10MB");
      return;
    }
    if (selectedUrl && selectedUrl.startsWith("blob:")) {
      URL.revokeObjectURL(selectedUrl);
    }
    const objectUrl = URL.createObjectURL(file);
    setSelectedUrl(objectUrl);
    setSelectedSource("upload");
    setSelectedFile(file);
    setCrop(null);
    setCompletedCrop(null);
    setActiveTab("crop");
  };

  // ── Crop ────────────────────────────────────────────────────────────────────

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    const initial = centerCrop(
      makeAspectCrop({ unit: "%", width: 90 }, aspectRatio, width, height),
      width, height
    );
    setCrop(initial);
    setCompletedCrop(initial);
  }, [aspectRatio]);

  const handleSave = async () => {
    const url      = selectedUrlRef.current;
    const source   = selectedSourceRef.current;
    const file     = selectedFileRef.current;
    const finalCrop = completedCropRef.current;

    if (saving) return;
    setSaveError("");

    if (!url) {
      setSaveError("Select an image first — search for one or upload a file.");
      return;
    }

    if (source === "upload" && file) {
      // Canvas crop → Firebase Storage upload
      setSaving(true);
      try {
        const imgEl = imgRef.current;
        const blob = (imgEl && finalCrop?.width)
          ? await cropToBlob(imgEl, finalCrop)
          : file;
        const path = `images/${tripId || "default"}/${Date.now()}.jpg`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, blob);
        const downloadUrl = await getDownloadURL(fileRef);
        setSaving(false);
        onSave({ url: downloadUrl });
      } catch (e) {
        setSaveError(`Upload failed: ${e.message}`);
        setSaving(false);
      }
    } else {
      // Pexels or current image — save URL + crop center as object-position params
      const cropParams = finalCrop ? cropToObjectPosition(finalCrop) : null;
      onSave({ url, cropParams });
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: `1px solid ${COLOR.borderWarm}`, marginBottom: 16 }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1, padding: "10px 4px", border: "none", background: "transparent",
              fontFamily: FONT.mono, fontSize: 11, letterSpacing: "0.04em",
              cursor: "pointer", color: activeTab === tab.id ? COLOR.mauve : COLOR.inkMid,
              borderBottom: activeTab === tab.id ? `2px solid ${COLOR.mauve}` : "2px solid transparent",
              transition: "all 0.15s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Pick tab ────────────────────────────────────────────────────────── */}
      {activeTab === "pick" && (
        <div>
          {pickLoading ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[0, 1, 2, 3, 4].map(i => (
                <div
                  key={i}
                  style={{
                    aspectRatio: "4/3", borderRadius: 8,
                    background: COLOR.buttonSecondary,
                    animation: "skeletonPulse 1.4s ease-in-out infinite",
                  }}
                />
              ))}
              <style>{`@keyframes skeletonPulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }`}</style>
            </div>
          ) : pickResults.length > 0 ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {pickResults.map((photo, i) => (
                  <div
                    key={photo.id || i}
                    onClick={() => handlePickSelect(photo, i)}
                    style={{
                      position: "relative",
                      borderRadius: 8, overflow: "hidden", cursor: "pointer",
                      aspectRatio: "4/3",
                      border: pickSelectedIdx === i ? `2px solid ${COLOR.mauveDark}` : "2px solid transparent",
                      transition: "border-color 0.15s",
                    }}
                    onMouseEnter={e => { if (pickSelectedIdx !== i) e.currentTarget.style.borderColor = COLOR.mauve; }}
                    onMouseLeave={e => { if (pickSelectedIdx !== i) e.currentTarget.style.borderColor = "transparent"; }}
                  >
                    <img
                      src={photo.thumb}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    {pickSelectedIdx === i && (
                      <div style={{
                        position: "absolute", top: 6, right: 6,
                        width: 18, height: 18, borderRadius: "50%",
                        background: COLOR.mauveDark, color: COLOR.white,
                        fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, textAlign: "center", marginTop: 8 }}>
                {getUseAIPhotoFilter() ? "✦ AI-curated · Powered by Pexels" : "Powered by Pexels"}
              </div>
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "24px 0", fontSize: 12, color: COLOR.inkMid, fontFamily: FONT.mono, opacity: 0.6 }}>
              No photos found — try Manual Search
            </div>
          )}
        </div>
      )}

      {/* ── Manual Search tab ─────────────────────────────────────────────── */}
      {activeTab === "search" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={focus} onBlur={blur}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              placeholder={activityTitle ? `${activityTitle}...` : "Search for a photo..."}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              onClick={handleSearch}
              disabled={searching || !searchQuery.trim()}
              style={{
                flexShrink: 0, border: `1px solid ${COLOR.mauve}44`,
                borderRadius: 8, background: COLOR.altCardGradient, color: COLOR.mauve,
                padding: "0 14px", cursor: searching ? "default" : "pointer",
                fontSize: 13, fontFamily: FONT.mono, height: 44,
                opacity: !searchQuery.trim() ? 0.5 : 1,
              }}
            >
              {searching ? "…" : "Search"}
            </button>
          </div>

          {searchError && (
            <div style={{ fontSize: 12, color: COLOR.mauveDark, fontFamily: FONT.mono, marginBottom: 10 }}>
              {searchError}
            </div>
          )}

          {searchResults.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
              {searchResults.map((photo, i) => (
                <div
                  key={i}
                  onClick={() => handleSelectSearchPhoto(photo)}
                  style={{
                    borderRadius: 8, overflow: "hidden", cursor: "pointer",
                    aspectRatio: "4/3", border: "2px solid transparent",
                    transition: "border-color 0.15s, transform 0.12s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = COLOR.mauve; e.currentTarget.style.transform = "scale(1.02)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "transparent"; e.currentTarget.style.transform = "scale(1)"; }}
                >
                  <img
                    src={photo.thumb}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </div>
              ))}
            </div>
          )}

          {!searching && searchResults.length === 0 && !searchError && (
            <div style={{ textAlign: "center", padding: "24px 0", fontSize: 12, color: COLOR.inkMid, fontFamily: FONT.mono, opacity: 0.6 }}>
              Search for a photo above
            </div>
          )}
        </div>
      )}

      {/* ── Upload tab ──────────────────────────────────────────────────────── */}
      {activeTab === "upload" && (
        <div>
          <label
            htmlFor="img-upload"
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              border: `2px dashed ${COLOR.borderWarm}`, borderRadius: 12, padding: "28px 16px",
              cursor: "pointer", color: COLOR.inkMid, fontFamily: FONT.mono, fontSize: 12,
              letterSpacing: "0.06em", gap: 8, transition: "border-color 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = COLOR.mauve}
            onMouseLeave={e => e.currentTarget.style.borderColor = COLOR.borderWarm}
          >
            <Icon name="imageUpload" size={28} color={COLOR.inkMid} />
            <span>Click to choose a file</span>
            <span style={{ opacity: 0.6, fontSize: 11 }}>JPG, PNG, WEBP</span>
            <input
              id="img-upload"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </label>
        </div>
      )}

      {/* ── Crop tab ────────────────────────────────────────────────────────── */}
      {activeTab === "crop" && (
        <div>
          {!selectedUrl ? (
            <div style={{ textAlign: "center", padding: "24px 0", fontSize: 12, color: COLOR.inkMid, fontFamily: FONT.mono, opacity: 0.6 }}>
              Select an image from Pick, Manual Search, or Upload first
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: 10, borderRadius: 10, overflow: "hidden", border: `1px solid ${COLOR.borderWarm}` }}>
                <ReactCrop
                  crop={crop}
                  onChange={(_c, pct) => setCrop(pct)}
                  onComplete={(_c, pct) => setCompletedCrop(pct)}
                  aspect={aspectRatio}
                  style={{ width: "100%" }}
                >
                  <img
                    ref={imgRef}
                    src={selectedUrl}
                    alt="Crop preview"
                    onLoad={onImageLoad}
                    crossOrigin={selectedSource === "search" ? "anonymous" : undefined}
                    style={{ width: "100%", display: "block" }}
                  />
                </ReactCrop>
              </div>
              <div style={{ fontSize: 10, color: COLOR.inkMid, fontFamily: FONT.mono, opacity: 0.6, textAlign: "center", marginBottom: 4 }}>
                {selectedSource === "upload" ? "Crop will be baked into the uploaded image" : "Crop position saved — original quality preserved"}
              </div>
              <div style={{ fontSize: 10, fontFamily: FONT.mono, color: COLOR.inkMid, textAlign: "center", marginBottom: 12, letterSpacing: "0.08em" }}>
                {context === "hero"
                  ? "DISPLAYED AT: 735\u00d7221px (day hero)"
                  : "DISPLAYED AT: 120\u00d790px (card thumbnail) \u00b7 622\u00d7350px (detail view)"}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Save / Cancel ────────────────────────────────────────────────────── */}
      {saveError && (
        <div style={{ fontSize: 12, color: COLOR.mauveDark, fontFamily: FONT.mono, padding: "8px 12px", background: COLOR.altCardGradient, borderRadius: 8, border: `1px solid ${COLOR.mauve}33`, marginTop: 12 }}>
          ⚠ {saveError}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
        <button
          onClick={e => { e.stopPropagation(); handleSave(); }}
          disabled={saving}
          style={{
            flex: 2, border: "none", borderRadius: 12, color: COLOR.white,
            padding: "14px", fontSize: 15, fontFamily: FONT.mono,
            minHeight: 52, cursor: saving ? "default" : "pointer",
            background: saving ? COLOR.inkMid : COLOR.mauve,
            transition: "background 0.2s",
          }}
        >
          {saving ? "Uploading…" : "Save Image"}
        </button>
        <button
          onClick={onCancel}
          style={{
            flex: 1, background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
            borderRadius: 12, color: COLOR.inkMid, padding: "14px",
            cursor: "pointer", fontSize: 15, fontFamily: FONT.mono, minHeight: 52,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
